import { useEffect, useState } from 'react';
import Mascot from '../../components/Mascot';
import { chapters, findSection } from '../../content/chapters';
import type { ParentSummary } from '../../lib/sync';

function timeAgo(ts: number): string {
  if (!ts) return 'no activity yet';
  const diff = Date.now() - ts;
  const h = Math.round(diff / 3_600_000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function ParentDashboard() {
  const [data, setData] = useState<ParentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/parent/summary')
      .then((r) => r.json() as Promise<ParentSummary & { error?: string }>)
      .then((d) => {
        if (d?.error) setError(d.error);
        else setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e));
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="p-8 text-center text-ink/60">Loading…</div>;
  if (error || !data)
    return (
      <div className="max-w-xl mx-auto p-6 space-y-3 text-center">
        <Mascot mood="thinking" size={80} className="mx-auto" />
        <h2 className="font-display text-xl">No data yet</h2>
        <p className="text-ink/70 text-sm">
          Once Grace solves a problem or two, her summary shows here.
        </p>
      </div>
    );

  return (
    <div className="max-w-2xl mx-auto px-5 py-8 space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mascot mood="happy" size={56} />
          <div>
            <div className="font-display font-black text-sky text-sm leading-none tracking-tight">
              Grace Math
            </div>
            <h1 className="font-display font-black text-2xl leading-none mt-1">
              Parent dashboard
            </h1>
            <p className="text-ink/60 text-sm">
              Last active {timeAgo(data.lastActive)}
            </p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Problems</div>
          <div className="font-display text-3xl">{data.problemsSolved}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Minutes</div>
          <div className="font-display text-3xl">{data.totalMinutes}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-xs text-ink/60">Streak</div>
          <div className="font-display text-3xl">{data.streak}</div>
        </div>
      </div>

      <div className="card p-5 space-y-3">
        <h2 className="font-display text-lg">Chapter understanding</h2>
        {chapters.map((c) => {
          const ids = c.sections.map((s) => s.id);
          const scores = ids.map((id) => data.chapterScores[id]).filter((v): v is number => typeof v === 'number');
          if (scores.length === 0) return null;
          const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
          return (
            <div key={c.num} className="flex items-center gap-3">
              <div className="w-6 text-ink/50 text-sm">{c.num}</div>
              <div className="flex-1 truncate text-sm">{c.title}</div>
              <div className="w-32 h-2 rounded-full bg-ink/5 overflow-hidden">
                <div className="h-full bg-sky" style={{ width: `${avg}%` }} />
              </div>
              <div className="w-10 text-right text-sm font-semibold">{avg}%</div>
            </div>
          );
        })}
      </div>

      <div className="card p-5 space-y-3">
        <h2 className="font-display text-lg">Recent problems</h2>
        {data.recentProblems.length === 0 && <div className="text-ink/60 text-sm">Nothing yet.</div>}
        {data.recentProblems.map((p, i) => {
          const sec = findSection(p.chapter);
          return (
            <div key={i} className="flex items-center justify-between py-2 border-b border-ink/5 last:border-0">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{p.topic}</div>
                <div className="text-xs text-ink/60">
                  {sec ? `Chapter ${sec.chapter.num} · ${sec.section.id}` : 'Misc'} · {p.input_type} · {timeAgo(p.timestamp)}
                </div>
              </div>
              <div className="text-sm font-semibold">
                {p.confidence === 'got_it' && <span className="text-sky">Got it</span>}
                {p.confidence === 'sorta' && <span className="text-ink/70">Sorta</span>}
                {p.confidence === 'lost' && <span className="text-coral">Lost</span>}
                {!p.confidence && <span className="text-ink/40">—</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
